import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { MaterialReactTable } from "material-react-table";
import {
  Container,
  Typography,
  Box,
  Input,
  LinearProgress,
  Button,
} from "@mui/material";
import { getToken, base_url } from "../../config/config";

function ExcelProcessor() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showOnlyWithPrice, setShowOnlyWithPrice] = useState(false);

  // Fetch data from API when the component mounts
  useEffect(() => {
    const fetchApiData = async () => {
      try {
        const response = await fetch(`${base_url}/v1/getBulkreport`);
        const result = await response.json();
        if (result.status === 1) {
          setApiData(result.data);
        } else {
          console.error("Error fetching API data: ", result);
        }
      } catch (error) {
        console.error("Error fetching API data:", error);
      }
    };

    fetchApiData();
  }, []);

  useEffect(() => {
    // Filter data when the showOnlyWithPrice flag changes
    if (showOnlyWithPrice) {
      setFilteredData(data.filter((row) => row.price !== "N/A"));
    } else {
      setFilteredData(data);
    }
  }, [data, showOnlyWithPrice]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      const binaryStr = event.target.result;
      const workbook = XLSX.read(binaryStr, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(sheet);
      processData(json);
    };

    reader.readAsBinaryString(file);
  };

  const processData = (json) => {
    const result = {};
    json.forEach((row) => {
      const key = row["Spalte1"];
      const time = parseFloat(row["az"]) || 0;
      if (result[key]) {
        result[key] += time;
      } else {
        result[key] = time;
      }
    });

    const finalData = Object.keys(result).map((key) => {
      // Find the API item where ANR matches the key
      const matchingApiItem = apiData.find((item) => item.ANR === key);
      const cost = result[key] * 60;
      const price = matchingApiItem
        ? parseFloat(matchingApiItem.Einzelpreis)
        : "N/A";
      const totalCostWithPrice =
        price !== "N/A" ? (cost + parseFloat(price)).toFixed(2) : "N/A";

      return {
        key,
        time: result[key].toFixed(2),
        cost: cost.toFixed(2),
        price: price !== "N/A" ? price.toFixed(2) : "N/A",
        totalCostWithPrice, // New column for total cost with price
      };
    });

    setData(finalData);
    setLoading(false);
    setUploading(false);
  };

  const exportToExcel = () => {
    // Prepare data for export
    const ws = XLSX.utils.json_to_sheet(filteredData, {
      header: ["key", "time", "cost", "price", "totalCostWithPrice"],
      skipHeader: false,
      cellDates: true,
    });

    // Define the workbook and worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    // Generate buffer
    XLSX.writeFile(wb, "data.xlsx");
  };

  return (
    <Container maxWidth='md' sx={{ padding: 4, textAlign: "center" }}>
      <Typography variant='h4' gutterBottom sx={{ marginBottom: 3 }}>
        Excel Time and Cost Calculator
      </Typography>
      <Input
        type='file'
        onChange={handleFileUpload}
        sx={{
          marginBottom: 3,
          padding: 1,
          border: "2px solid #ddd",
          borderRadius: 1,
          fontSize: 16,
          cursor: "pointer",
        }}
      />
      <Box sx={{ marginTop: 2, marginBottom: 2 }}>
        <Button
          variant='contained'
          onClick={() => setShowOnlyWithPrice(!showOnlyWithPrice)}>
          {showOnlyWithPrice ? "Show All" : "Show Only With Price"}
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={exportToExcel}
          sx={{ marginLeft: 2 }}>
          Export to Excel
        </Button>
      </Box>
      {(uploading || loading) && (
        <Box sx={{ width: "100%", marginTop: 2 }}>
          <LinearProgress />
        </Box>
      )}
      {filteredData.length > 0 && !loading && (
        <Box mt={3}>
          <MaterialReactTable
            columns={[
              { accessorKey: "key", header: "Key" },
              {
                accessorKey: "time",
                header: "Total Time",
                Cell: ({ cell }) => parseFloat(cell.getValue()).toFixed(2),
              },
              {
                accessorKey: "cost",
                header: "Total Cost (Euro)",
                Cell: ({ cell }) => parseFloat(cell.getValue()).toFixed(2),
              },
              {
                accessorKey: "price",
                header: "Einzelpreis",
                Cell: ({ cell }) =>
                  cell.getValue() === "N/A"
                    ? "N/A"
                    : parseFloat(cell.getValue()).toFixed(2),
              },
              {
                accessorKey: "totalCostWithPrice",
                header: "Total Cost with Price",
                Cell: ({ cell }) =>
                  cell.getValue() === "N/A"
                    ? "N/A"
                    : parseFloat(cell.getValue()).toFixed(2),
              },
            ]}
            data={filteredData}
            enableSorting
            enablePagination
            initialState={{
              pagination: { pageIndex: 0, pageSize: 5 },
              sorting: [{ id: "key", desc: false }],
            }}
            muiTableProps={{
              sx: {
                borderCollapse: "collapse",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#f4f4f4",
                color: "#333",
              },
            }}
            muiTableBodyRowProps={{
              sx: {
                "&:nth-of-type(even)": {
                  backgroundColor: "#f9f9f9",
                },
                "&:hover": {
                  backgroundColor: "#f1f1f1",
                },
              },
            }}
          />
        </Box>
      )}
    </Container>
  );
}

export default ExcelProcessor;
